/*
  Новый лицевой счёт руками - мастер из шести шагов (20.10.2026, решение владельца: вариант
  «А» из макетов). Помещение, должники, начисления, оплаты, проживающие, итог.

  Счёт сохраняется на первом шаге, остальное дописывается: человек может уйти с любого
  шага, ничего не потеряв, и вернуться на карточку (она показывает всё то же самое
  вкладками). Все шесть шагов видны сразу (решение владельца 21.10.2026); до заведения
  счёта шаги со второго по шестой объясняют, что без счёта им некуда писать. Шаги со второго по пятый - те же экраны, что на карточке: ничего не
  дублируется, окна внесения у них общие.

  Файл как первичное внесение остаётся рядом: ссылка «Загрузить файл вместо ручного
  ввода» ведёт в раздел «Загрузка».
*/

import { useCallback, useState } from 'react'

import { api } from '../api/client.js'
import { can, has } from '../components/access.js'
import { Loading, Notice } from '../components/Common.jsx'
import AccountForm from '../components/entry/AccountForm.jsx'
import AccountParticipants from '../components/entry/AccountParticipants.jsx'
import ChargesTab from '../components/entry/ChargesTab.jsx'
import NewAccountLeave from '../components/entry/NewAccountLeave.jsx'
import PaymentsTab from '../components/entry/PaymentsTab.jsx'
import ResidentsTab from '../components/entry/ResidentsTab.jsx'
import useEntryList from '../components/entry/useEntryList.js'
import WizardSummary from '../components/entry/WizardSummary.jsx'
import Tabs from '../components/Tabs.jsx'

const STEPS = [
  { key: 'place', title: '1. Помещение' },
  { key: 'people', title: '2. Должники' },
  { key: 'charges', title: '3. Начисления' },
  { key: 'payments', title: '4. Оплаты' },
  { key: 'residents', title: '5. Проживающие' },
  { key: 'done', title: '6. Готово' },
]

const LEAVE_LINKS = [
  { to: '/import', title: 'Загрузить файл вместо ручного ввода' },
  { to: '/registry', title: 'К реестру' },
]

function Pager({ step, onStep }) {
  const index = STEPS.findIndex((item) => item.key === step)
  return (
    <div className="row row--tight">
      {index > 0 ? (
        <button className="button button--quiet" type="button" onClick={() => onStep(STEPS[index - 1].key)}>
          Назад
        </button>
      ) : null}
      {index < STEPS.length - 1 ? (
        <button className="button" type="button" onClick={() => onStep(STEPS[index + 1].key)}>
          Дальше: {STEPS[index + 1].title.slice(3).toLowerCase()}
        </button>
      ) : null}
    </div>
  )
}

function NeedAccount({ onBack }) {
  return (
    <Notice>
      <p>
        Этот шаг станет доступен, когда счёт будет заведён: данные записываются в счёт, а счёта
        ещё нет. Заведите его на первом шаге - номер и адрес достаточно.
      </p>
      <button className="button" type="button" onClick={onBack}>
        Перейти к первому шагу
      </button>
    </Notice>
  )
}

function PeopleStep({ accountId, onDone }) {
  const load = useCallback(() => api.account(accountId), [accountId])
  const { rows: card, error, reload } = useEntryList(load)
  if (error) return <Notice error={error} />
  if (!card) return <Loading what="Загружаем карточку" />
  return (
    <AccountParticipants
      card={card}
      canEdit
      onDone={(text) => {
        reload()
        onDone(text)
      }}
    />
  )
}

export default function AccountNewPage({ profile }) {
  const [account, setAccount] = useState(null)
  const [step, setStep] = useState('place')
  const [notice, setNotice] = useState(null)
  const [typed, setTyped] = useState(false)

  // Заводит тот, кто подаёт заявления (право собирать документы): сервер откажет
  // остальным, а страница не обещает недоступного.
  if (!has(profile, 'build_documents')) {
    return (
      <Notice>
        Заводить счета могут те, кто подаёт заявления. Обратитесь к руководителю или юристу.
      </Notice>
    )
  }
  // Лицензия не оплачена (аудит 26.10.2026, причина 2): заводить нельзя, и мастер из шести
  // шагов не показывается вовсе - иначе человек заполнил бы форму и получил отказ на первом шаге.
  if (!can(profile, 'build_documents')) {
    return (
      <Notice kind="warn" wide>
        <div className="notice__what">Лицензия не оплачена, поэтому счета заводить нельзя.</div>
        <div className="notice__do">
          Оплатите лицензию - заведение счетов восстановится сразу после поступления средств.
        </div>
      </Notice>
    )
  }

  const created = (result) => {
    setAccount(result)
    setStep('people')
  }
  const again = () => {
    setAccount(null)
    setStep('place')
    setNotice(null)
  }
  const done = (text) => setNotice(text)

  return (
    <>
      <header className="head">
        <div>
          <h1 className="head__title">Новый лицевой счёт</h1>
          <div className="head__note">
            Вносите по шагам; любой шаг можно пропустить и вернуться на карточке счёта.
          </div>
        </div>
        <div className="row">
          <NewAccountLeave dirty={typed && !account} links={LEAVE_LINKS} />
        </div>
      </header>

      <Tabs tabs={STEPS} active={step} onChange={setStep} nested />
      {notice ? <Notice kind="ok">{notice}</Notice> : null}

      {/* 21.10.2026, решение владельца: все шаги видны сразу, а не открываются после
          создания счёта. Начисления, оплаты и жильцов некуда записать без счёта, поэтому
          до его создания шаг объясняет, чего ему не хватает, и ведёт на первый. */}
      {!account && step !== 'place' ? <NeedAccount onBack={() => setStep('place')} /> : null}

      {/* Заведённый счёт на первом шаге показывается, а не заводится снова (21.10.2026,
          ревью): пустая форма после «Назад» заводила второй счёт, а первый оставался
          в реестре без начислений. Номер и адрес правятся на карточке счёта. */}
      {/* Форма не размонтируется при переходе на другой шаг (аудит 28.10.2026, раздел 5.3):
          иначе набранные номер и адрес стирались, стоило заглянуть в соседний шаг. */}
      {!account ? (
        <div hidden={step !== 'place'}>
          <AccountForm onCreated={created} onDirty={setTyped} />
        </div>
      ) : null}
      {step === 'place' && account ? (
        <Notice kind="ok">
          Счёт № {account.number} заведён: {account.address}. Номер и адрес можно поправить на
          карточке счёта; остальное - на следующих шагах или там же.
        </Notice>
      ) : null}
      {account && step === 'people' ? <PeopleStep accountId={account.account_id} onDone={done} /> : null}
      {account && step === 'charges' ? (
        <ChargesTab accountId={account.account_id} canEdit onDone={done} />
      ) : null}
      {account && step === 'payments' ? (
        <PaymentsTab accountId={account.account_id} canEdit onDone={done} />
      ) : null}
      {account && step === 'residents' ? (
        <ResidentsTab accountId={account.account_id} canEdit onDone={done} />
      ) : null}
      {account && step === 'done' ? (
        <WizardSummary accountId={account.account_id} number={account.number} onAnother={again} />
      ) : null}
      {account && step !== 'done' ? <Pager step={step} onStep={setStep} /> : null}
    </>
  )
}
